import { useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, Settings2 } from "lucide-react";
import React, { useMemo, useState } from "react";

import { getCashflowCalendar, getCashflowSettings } from "@/api/cashflow";
import { useConnection } from "@/auth/ConnectionContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatRub } from "@/lib/format";
import { AttentionPanel } from "@/pages/cashflow/AttentionPanel";
import { CalendarTable } from "@/pages/cashflow/CalendarTable";
import { EntriesDialog } from "@/pages/cashflow/EntriesDialog";
import { ManualPanel } from "@/pages/cashflow/ManualPanel";
import { ReceivablesCheckPanel } from "@/pages/cashflow/ReceivablesCheckPanel";
import { ReceivablesPanel } from "@/pages/cashflow/ReceivablesPanel";
import { SettingsDialog } from "@/pages/cashflow/SettingsDialog";
import {
  ALL_ORGS,
  errorDetail,
  errorStatus,
  todayIso,
  type CashflowCellSelection,
} from "@/pages/cashflow/utils";

export function CashflowPage(): React.ReactElement {
  const qc = useQueryClient();
  // Календарь считается из активного подключения к 1С: без него в ключе кэша
  // после смены базы страница показала бы цифры предыдущей как свои.
  const { activeConnection } = useConnection();
  const [asOf, setAsOf] = useState(todayIso);
  const [orgTab, setOrgTab] = useState<string>(ALL_ORGS);
  const [showSettings, setShowSettings] = useState(false);
  // Ячейка, по которой кликнули: `null` — диалог детализации закрыт.
  const [cell, setCell] = useState<CashflowCellSelection | null>(null);

  const settingsQ = useQuery({
    queryKey: ["cashflow-settings"],
    queryFn: getCashflowSettings,
  });
  const calendarQ = useQuery({
    // Пустое поле «На дату» — это «сегодня по бэку», отдельный ключ кэша.
    queryKey: ["cashflow-calendar", activeConnection?.id ?? "none", asOf || "today"],
    queryFn: () => getCashflowCalendar(asOf || undefined),
  });

  // При ошибке react-query держит прошлый ответ: показывать его рядом с
  // баннером нельзя — это устаревший календарь, выданный за пересчитанный.
  const calendar = calendarQ.isError ? undefined : calendarQ.data;
  const orgs = useMemo(() => calendar?.orgs ?? [], [calendar]);
  const selected = calendar?.calendars.find((c) => c.org_inn === orgTab);
  const invalidateCalendar = () =>
    qc.invalidateQueries({ queryKey: ["cashflow-calendar"] });

  // Дата для ручных строк — та, на которую ПОСЧИТАЛ БЭК (в ответе), а не
  // выбранная в поле: при пустом поле это московское «сегодня», и с ним же
  // бэк сверяет даты строк. Браузерное «сегодня» тут не годится — в другом
  // часовом поясе форма разрешила бы дату, на которую бэк ответит 422.
  // Ответа ещё нет (грузится или ошибка) — остаётся выбранное в поле.
  const manualAsOf = calendar?.as_of || asOf;
  const calendarError = calendarQ.isError ? errorDetail(calendarQ.error) : null;
  // Подсказку про подключение вешаем не на любой 422, а только на тот, где бэк
  // прямо говорит про конфигурацию 1С.
  const wrongConnection =
    calendarError !== null &&
    errorStatus(calendarQ.error) === 422 &&
    (calendarError.includes("УНФ") || calendarError.includes("config_type"));

  return (
    <div className="space-y-6 p-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Финплан</h1>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Платёжный календарь по неделям: остаток на расчётных счетах УНФ на дату расчёта
            плюс записи из 1С и ручных строк. Ничего не сохраняется — при каждом открытии
            считается заново.
          </p>
        </div>
        <Button variant="outline" onClick={() => setShowSettings(true)} disabled={!settingsQ.data}>
          <Settings2 className="mr-2 h-4 w-4" />
          Настройки
        </Button>
      </header>

      <div className="flex flex-wrap items-end gap-3 rounded-md border p-4">
        <div className="space-y-1.5">
          <Label htmlFor="as_of">На дату</Label>
          <Input
            id="as_of"
            type="date"
            className="w-44"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
          />
        </div>
        <Button variant="outline" onClick={() => invalidateCalendar()} disabled={calendarQ.isFetching}>
          <RefreshCw className={`mr-2 h-4 w-4 ${calendarQ.isFetching ? "animate-spin" : ""}`} />
          Пересчитать
        </Button>
        {settingsQ.data && (
          <p className="text-xs text-muted-foreground">
            Горизонт: {settingsQ.data.horizon_weeks} нед. · организаций в плане:{" "}
            {settingsQ.data.org_inns.length}
          </p>
        )}
      </div>

      {calendarError !== null && (
        <div className="space-y-2 rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800">
          <p>Календарь не пересчитался: {calendarError}</p>
          {wrongConnection && (
            <p className="text-xs">
              Переключите активное подключение к 1С на УНФ в шапке страницы — финплан читает
              остатки и документы только из УНФ.
            </p>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => void calendarQ.refetch()}
            disabled={calendarQ.isFetching}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${calendarQ.isFetching ? "animate-spin" : ""}`} />
            Повторить
          </Button>
        </div>
      )}

      {calendarQ.isLoading && (
        <p className="text-sm text-muted-foreground">Считаем календарь…</p>
      )}

      {calendar && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant={orgTab === ALL_ORGS ? "default" : "outline"}
              onClick={() => setOrgTab(ALL_ORGS)}
            >
              Все
            </Button>
            {orgs.map((o) => (
              <Button
                key={o.inn}
                size="sm"
                variant={orgTab === o.inn ? "default" : "outline"}
                onClick={() => setOrgTab(o.inn)}
              >
                {o.name}
              </Button>
            ))}
            <p className="ml-auto text-xs text-muted-foreground">
              За горизонтом: приход {formatRub(calendar.beyond_horizon_in)}, расход{" "}
              {formatRub(calendar.beyond_horizon_out)}
            </p>
          </div>

          {selected ? (
            <CalendarTable
              calendar={selected}
              weeks={calendar.weeks}
              entries={calendar.entries}
              orgInn={orgTab}
              onCellClick={setCell}
            />
          ) : (
            <p className="text-sm text-rose-600">
              В ответе нет календаря для выбранной организации ({orgTab}) — выберите другую
              вкладку.
            </p>
          )}

          <AttentionPanel
            attention={calendar.attention}
            sources={calendar.sources}
            orgs={orgs}
          />

          <ReceivablesCheckPanel
            asOf={calendar.as_of}
            orgs={orgs}
            orgInn={orgTab}
            connectionId={activeConnection?.id ?? null}
          />

          <ReceivablesPanel
            receivables={calendar.receivables}
            sources={calendar.sources}
            orgs={orgs}
            orgInn={orgTab}
          />
        </>
      )}

      {settingsQ.isError && (
        <div className="rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800">
          Настройки финплана не загрузились: {errorDetail(settingsQ.error)}
        </div>
      )}

      {/* Без даты расчёта (поле пустое, а календарь ещё не ответил) панель
          не показываем: свою дату выдумывать нечестно — от неё зависят и
          список строк, и граница «не в прошлом». */}
      {settingsQ.data && manualAsOf && (
        <ManualPanel
          orgInns={settingsQ.data.org_inns}
          orgs={orgs}
          asOf={manualAsOf}
          onChanged={invalidateCalendar}
        />
      )}

      {cell !== null && calendar && (
        <EntriesDialog
          selection={cell}
          // Итоговые строки (`category === null`) детализируем без подписи статьи;
          // ключ без строки в ответе показываем как есть, а не прячем.
          categoryLabel={
            cell.category === null
              ? null
              : (selected?.rows.find((r) => r.category === cell.category)?.label ??
                cell.category)
          }
          entries={calendar.entries}
          orgs={orgs}
          weeks={calendar.weeks}
          orgInn={orgTab}
          onClose={() => setCell(null)}
        />
      )}

      {showSettings && settingsQ.data && (
        <SettingsDialog settings={settingsQ.data} onClose={() => setShowSettings(false)} />
      )}
    </div>
  );
}
